import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { openPrintWindow } from "@/utils/printHelper";
import {
  Plus,
  Search,
  Eye,
  Edit,
  MessageSquare,
  Copy,
  Trash2,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Package,
  Check,
  Settings,
  Image,
  Link,
  Printer,
  QrCode,
  ChevronsUpDown,
  Upload,
  ArrowLeft,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { PasswordInput } from "@/components/ui/password-input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default color options for printing
const DEFAULT_PRINT_COLORS = ["Blue", "Black", "Red"];

// Top Paper Type options
const TOP_PAPER_TYPES = [
  "Plain Kraft",
  "Printed Kraft",
  "Plain Duplex",
  "Plain GYT",
  "Printed GYT",
  "Plain Imported",
  "Printed Imported",
];

// GSM configuration based on ply
const getGSMConfig = (ply) => {
  const configs = {
    3: [
      { name: "Liner 1", type: "liner" },
      { name: "Flute 1", type: "flute" },
      { name: "Liner 2", type: "liner" },
    ],
    5: [
      { name: "Liner 1", type: "liner" },
      { name: "Flute 1", type: "flute" },
      { name: "Liner 2", type: "liner" },
      { name: "Flute 2", type: "flute" },
      { name: "Liner 3", type: "liner" },
    ],
    7: [
      { name: "Liner 1", type: "liner" },
      { name: "Flute 1", type: "flute" },
      { name: "Liner 2", type: "liner" },
      { name: "Flute 2", type: "flute" },
      { name: "Liner 3", type: "liner" },
      { name: "Flute 3", type: "flute" },
      { name: "Liner 4", type: "liner" },
    ],
    9: [
      { name: "Liner 1", type: "liner" },
      { name: "Flute 1", type: "flute" },
      { name: "Liner 2", type: "liner" },
      { name: "Flute 2", type: "flute" },
      { name: "Liner 3", type: "liner" },
      { name: "Flute 3", type: "flute" },
      { name: "Liner 4", type: "liner" },
      { name: "Flute 4", type: "flute" },
      { name: "Liner 5", type: "liner" },
    ],
    11: [
      { name: "Liner 1", type: "liner" },
      { name: "Flute 1", type: "flute" },
      { name: "Liner 2", type: "liner" },
      { name: "Flute 2", type: "flute" },
      { name: "Liner 3", type: "liner" },
      { name: "Flute 3", type: "flute" },
      { name: "Liner 4", type: "liner" },
      { name: "Flute 4", type: "flute" },
      { name: "Liner 5", type: "liner" },
      { name: "Flute 5", type: "flute" },
      { name: "Liner 6", type: "liner" },
    ],
  };
  return configs[ply] || configs[3];
};

// Generate next product ID in #PRID-XXX format
const generateNextProductId = (existingSpecs) => {
  let maxNum = 0;
  existingSpecs.forEach((spec) => {
    const match = spec.product_id?.match(/^#PRID-(\d+)$/i);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNum) maxNum = num;
    }
  });
  const nextNum = maxNum + 1;
  return `#PRID-${String(nextNum).padStart(3, "0")}`;
};

const ProdMaterialInfo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [specifications, setSpecifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Dialog states
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [showCommentDialog, setShowCommentDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showEraseDialog, setShowEraseDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [eraseType, setEraseType] = useState(""); // "selected" or "all"
  const [erasePassword, setErasePassword] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);
  const [qrSpec, setQrSpec] = useState(null);
  const qrRef = useRef(null);
  const [editingSpec, setEditingSpec] = useState(null);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [newComment, setNewComment] = useState("");
  const [saving, setSaving] = useState(false);

  // Dropdown options from API
  const [dieIds, setDieIds] = useState([]);
  const [calicoColors, setCalicoColors] = useState(["Blue", "Green", "Red"]);
  const [numberCalicoOptions, setNumberCalicoOptions] = useState(["1", "2", "4"]);
  const [customers, setCustomers] = useState([]);
  const [companyOpen, setCompanyOpen] = useState(false);

  // Color management for printing
  const [availablePrintColors, setAvailablePrintColors] = useState(DEFAULT_PRINT_COLORS);
  const [newColorInput, setNewColorInput] = useState("");
  const [showAddColor, setShowAddColor] = useState(false);

  // New option inputs
  const [newCalicoColorInput, setNewCalicoColorInput] = useState("");
  const [showAddCalicoColor, setShowAddCalicoColor] = useState(false);
  const [newNumberCalicoInput, setNewNumberCalicoInput] = useState("");
  const [showAddNumberCalico, setShowAddNumberCalico] = useState(false);
  
  // Top Paper Type management
  const [topPaperTypes, setTopPaperTypes] = useState(TOP_PAPER_TYPES);
  const [newPaperTypeInput, setNewPaperTypeInput] = useState("");
  const [showAddPaperType, setShowAddPaperType] = useState(false);
  
  // Raw materials for GSM dropdown
  const [rawMaterials, setRawMaterials] = useState([]);
  const [gsmDropdownOpen, setGsmDropdownOpen] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    product_id: "",
    company_name: "",
    item_name: "",
    date: "",
    length: "",
    breadth: "",
    height: "",
    dimension_unit: "Inches",  // Inches | MM | Cm — single unit shared by L/B/H
    reel_size: "",
    cutting_size: "",
    ply: 3,
    gsm_config: [],
    printing_enabled: "No",
    colors: [],
    no_of_colors: "",
    top_paper_type: "",
    die_id: "",
    die_ids: [],
    calico_color: "",
    number_calico: "",
    stitching: "",
    rate: "",
    shared_across_companies: false,  // When true, picking this spec does not force the WO customer
    image_link: "",  // Keep for backward compatibility (also used as primary image)
    image_links: [], // Array of {url, filename, file_id, is_primary} for multiple images
    primary_image_url: "", // URL of the primary image for QR code in table
  });

  useEffect(() => {
    fetchSpecifications();
    fetchDropdownOptions();
    loadSavedColors();
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      // Fetch Die IDs
      const dieResponse = await axios.get(`${API}/die-ids`);
      setDieIds(dieResponse.data);

      // Fetch Calico Colors
      const calicoResponse = await axios.get(`${API}/dropdown-options/calico_colors`);
      if (calicoResponse.data?.options) {
        setCalicoColors(calicoResponse.data.options);
      }

      // Fetch Number Calico
      const numCalicoResponse = await axios.get(`${API}/dropdown-options/number_calico`);
      if (numCalicoResponse.data?.options) {
        setNumberCalicoOptions(numCalicoResponse.data.options);
      }
      
      // Fetch Top Paper Types
      const paperResponse = await axios.get(`${API}/dropdown-options/top_paper_types`);
      if (paperResponse.data?.options) {
        setTopPaperTypes(paperResponse.data.options);
      }

      // Fetch Customers from WO Database
      const customersResponse = await axios.get(`${API}/wo-customers`);
      if (customersResponse.data) {
        setCustomers(customersResponse.data);
      }
      
      // Fetch Raw Materials for GSM dropdown
      const materialsResponse = await axios.get(`${API}/materials`);
      if (materialsResponse.data) {
        setRawMaterials(materialsResponse.data);
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const loadSavedColors = () => {
    const saved = localStorage.getItem("prod_material_print_colors");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setAvailablePrintColors([...new Set([...DEFAULT_PRINT_COLORS, ...parsed])]);
      } catch (e) {
        console.error("Error loading saved colors:", e);
      }
    }
  };

  const saveColors = (colors) => {
    const customColors = colors.filter((c) => !DEFAULT_PRINT_COLORS.includes(c));
    localStorage.setItem("prod_material_print_colors", JSON.stringify(customColors));
  };

  const fetchSpecifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/product-specifications`);
      setSpecifications(response.data);
    } catch (error) {
      console.error("Error fetching specifications:", error);
      toast.error("Failed to load product specifications");
    } finally {
      setLoading(false);
    }
  };

  // Filter specifications based on search
  const filteredSpecs = specifications.filter((spec) => {
    const search = searchTerm.toLowerCase();
    return (
      spec.product_id?.toLowerCase().includes(search) ||
      spec.company_name?.toLowerCase().includes(search) ||
      spec.item_name?.toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredSpecs.length / itemsPerPage);
  const paginatedSpecs = filteredSpecs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const resetForm = (autoGenerateId = true) => {
    const newProductId = autoGenerateId
      ? generateNextProductId(specifications)
      : "";
    setFormData({
      product_id: newProductId,
      company_name: "",
      item_name: "",
      date: "",
      length: "",
      breadth: "",
      height: "",
      reel_size: "",
      cutting_size: "",
      ply: 3,
      gsm_config: getGSMConfig(3).map((item) => ({ ...item, gsm: "" })),
      printing_enabled: "No",
      colors: [],
      no_of_colors: "",
      top_paper_type: "",
      die_id: "",
      calico_color: "",
      number_calico: "",
      stitching: "",
      rate: "",
      image_link: "",
      image_links: [],
      primary_image_url: "",
    });
    setEditingSpec(null);
  };

  const handleAddNew = () => {
    resetForm(true);
    setShowAddEditDialog(true);
  };

  const handleEdit = (spec) => {
    setEditingSpec(spec);
    let colors = [];
    if (spec.colors && Array.isArray(spec.colors)) {
      colors = spec.colors;
    } else if (spec.color && typeof spec.color === "string" && spec.color) {
      colors = spec.color.split(",").map((c) => c.trim()).filter(Boolean);
    }

    setFormData({
      product_id: spec.product_id || "",
      company_name: spec.company_name || "",
      item_name: spec.item_name || "",
      date: spec.date || "",
      length: spec.length?.toString() || "",
      breadth: spec.breadth?.toString() || "",
      height: spec.height?.toString() || "",
      dimension_unit: spec.dimension_unit || "Inches",
      reel_size: spec.reel_size?.toString() || "",
      cutting_size: spec.cutting_size?.toString() || "",
      ply: spec.ply || 3,
      gsm_config:
        spec.gsm_config?.length > 0
          ? spec.gsm_config
          : getGSMConfig(spec.ply || 3).map((item) => ({ ...item, gsm: "" })),
      printing_enabled: spec.printing_enabled || (spec.printing === "Yes" ? "Yes" : "No"),
      colors: colors,
      no_of_colors: spec.no_of_colors?.toString() || "",
      top_paper_type: spec.top_paper_type || "",
      die_id: spec.die_id || "",
      die_ids: spec.die_ids || (spec.die_id ? [spec.die_id] : []),
      calico_color: spec.calico_color || "",
      number_calico: spec.number_calico || "",
      stitching: spec.stitching || "",
      rate: spec.rate?.toString() || "",
      shared_across_companies: spec.shared_across_companies === true,
      image_link: spec.image_link || "",
      image_links: spec.image_links || [],
      primary_image_url: spec.primary_image_url || spec.image_link || "",
    });
    setShowAddEditDialog(true);
  };

  const handlePlyChange = (newPly) => {
    const plyInt = parseInt(newPly);
    const newGsmConfig = getGSMConfig(plyInt).map((item) => ({ ...item, gsm: "" }));
    setFormData((prev) => ({
      ...prev,
      ply: plyInt,
      gsm_config: newGsmConfig,
    }));
  };

  const handleGSMChange = (index, value) => {
    setFormData((prev) => {
      const newConfig = [...prev.gsm_config];
      newConfig[index] = { ...newConfig[index], gsm: value };
      return { ...prev, gsm_config: newConfig };
    });
  };

  const handleColorToggle = (color) => {
    setFormData((prev) => {
      const currentColors = prev.colors || [];
      if (currentColors.includes(color)) {
        return { ...prev, colors: currentColors.filter((c) => c !== color) };
      } else {
        return { ...prev, colors: [...currentColors, color] };
      }
    });
  };

  const handleAddNewColor = () => {
    if (newColorInput.trim()) {
      const newColor = newColorInput.trim();
      if (!availablePrintColors.includes(newColor)) {
        const updatedColors = [...availablePrintColors, newColor];
        setAvailablePrintColors(updatedColors);
        saveColors(updatedColors);
      }
      setFormData((prev) => ({
        ...prev,
        colors: [...(prev.colors || []), newColor],
      }));
      setNewColorInput("");
      setShowAddColor(false);
    }
  };

  const handleAddCalicoColor = async () => {
    if (newCalicoColorInput.trim()) {
      try {
        await axios.post(`${API}/dropdown-options/calico_colors`, {
          option: newCalicoColorInput.trim(),
        });
        setCalicoColors((prev) => [...prev, newCalicoColorInput.trim()]);
        setNewCalicoColorInput("");
        setShowAddCalicoColor(false);
        toast.success("Color added");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add color");
      }
    }
  };

  const handleDeleteCalicoColor = async (color) => {
    try {
      await axios.delete(`${API}/dropdown-options/calico_colors/${encodeURIComponent(color)}`);
      setCalicoColors((prev) => prev.filter((c) => c !== color));
      toast.success("Color deleted");
    } catch (error) {
      toast.error("Failed to delete color");
    }
  };

  const handleAddNumberCalico = async () => {
    if (newNumberCalicoInput.trim()) {
      try {
        await axios.post(`${API}/dropdown-options/number_calico`, {
          option: newNumberCalicoInput.trim(),
        });
        setNumberCalicoOptions((prev) => [...prev, newNumberCalicoInput.trim()]);
        setNewNumberCalicoInput("");
        setShowAddNumberCalico(false);
        toast.success("Option added");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add option");
      }
    }
  };

  const handleDeleteNumberCalico = async (option) => {
    try {
      await axios.delete(`${API}/dropdown-options/number_calico/${encodeURIComponent(option)}`);
      setNumberCalicoOptions((prev) => prev.filter((o) => o !== option));
      toast.success("Option deleted");
    } catch (error) {
      toast.error("Failed to delete option");
    }
  };
  
  // Paper Type Management
  const handleAddPaperType = async () => {
    if (newPaperTypeInput.trim()) {
      try {
        await axios.post(`${API}/dropdown-options/top_paper_types`, {
          option: newPaperTypeInput.trim(),
        });
        setTopPaperTypes((prev) => [...prev, newPaperTypeInput.trim()]);
        setNewPaperTypeInput("");
        setShowAddPaperType(false);
        toast.success("Paper type added");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add paper type");
      }
    }
  };

  const handleDeletePaperType = async (paperType) => {
    try {
      await axios.delete(`${API}/dropdown-options/top_paper_types/${encodeURIComponent(paperType)}`);
      setTopPaperTypes((prev) => prev.filter((t) => t !== paperType));
      toast.success("Paper type deleted");
    } catch (error) {
      toast.error("Failed to delete paper type");
    }
  };

  const handleSave = async () => {
    if (!formData.product_id || !formData.company_name || !formData.item_name) {
      toast.error("Please fill in Product ID, Company Name, and Item Name");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        product_id: formData.product_id,
        company_name: formData.company_name,
        item_name: formData.item_name,
        date: formData.date,
        length: formData.length,
        breadth: formData.breadth,
        height: formData.height,
        dimension_unit: formData.dimension_unit || "Inches",
        reel_size: formData.reel_size,
        cutting_size: formData.cutting_size,
        ply: formData.ply,
        gsm_config: formData.gsm_config,
        printing_enabled: formData.printing_enabled,
        printing: formData.printing_enabled,
        colors: formData.colors || [],
        color: (formData.colors || []).join(", "),
        no_of_colors: formData.no_of_colors ? parseInt(formData.no_of_colors) : null,
        top_paper_type: formData.top_paper_type,
        die_id: formData.die_ids?.length > 0 ? formData.die_ids[0] : formData.die_id,
        die_ids: formData.die_ids || [],
        calico_color: formData.calico_color,
        number_calico: formData.number_calico,
        stitching: formData.stitching,
        rate: formData.rate ? parseFloat(formData.rate) : null,
        shared_across_companies: !!formData.shared_across_companies,
        image_link: formData.image_link || "",
        image_links: formData.image_links || [],
        primary_image_url: formData.primary_image_url || formData.image_link || (formData.image_links?.[0]?.url || ""),
      };

      if (editingSpec) {
        await axios.put(`${API}/product-specifications/${editingSpec.id}`, payload);
        toast.success("Product specification updated successfully");
      } else {
        await axios.post(`${API}/product-specifications`, payload);
        toast.success("Product specification created successfully");
      }

      setShowAddEditDialog(false);
      resetForm(false);
      fetchSpecifications();
      // Sync die ID statuses
      axios.post(`${API}/die-ids/sync-status`).catch(() => {});
    } catch (error) {
      console.error("Error saving specification:", error);
      toast.error(error.response?.data?.detail || "Failed to save product specification");
    } finally {
      setSaving(false);
    }
  };

  const handleView = (spec) => {
    navigate(`/workorder/prod-material/${spec.id}`);
  };

  // QR Code Print functionality
  const handlePrintQR = (spec) => {
    // Use primary_image_url first, then fall back to image_link, then first image in array
    const primaryUrl = spec.primary_image_url || spec.image_link || spec.image_links?.[0]?.url;
    if (!primaryUrl) {
      toast.error("No image link available for this product");
      return;
    }
    // Set qrSpec with the primary URL
    setQrSpec({ ...spec, primary_url: primaryUrl });
    setShowQRDialog(true);
  };

  const printQRCode = () => {
    const printContent = qrRef.current;
    if (!printContent) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code - ${qrSpec?.product_id}</title>
        <style>
          body {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            margin: 0;
            font-family: Arial, sans-serif;
          }
          .qr-container {
            text-align: center;
            padding: 20px;
          }
          .product-info {
            margin-bottom: 15px;
          }
          .product-id {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .product-name {
            font-size: 14px;
            color: #666;
          }
          .qr-code {
            margin: 20px 0;
          }
          .scan-text {
            font-size: 12px;
            color: #888;
            margin-top: 10px;
          }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          <div class="product-info">
            <div class="product-id">${qrSpec?.product_id}</div>
            <div class="product-name">${qrSpec?.item_name}</div>
          </div>
          <div class="qr-code">
            ${printContent.innerHTML}
          </div>
          <div class="scan-text">Scan to view product image</div>
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    openPrintWindow(htmlContent);
  };

  const handleComment = (spec) => {
    setSelectedSpec(spec);
    setNewComment("");
    setShowCommentDialog(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      await axios.post(`${API}/product-specifications/${selectedSpec.id}/comments`, {
        text: newComment,
      });
      toast.success("Comment added successfully");
      setNewComment("");
      fetchSpecifications();
      const response = await axios.get(`${API}/product-specifications/${selectedSpec.id}`);
      setSelectedSpec(response.data);
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/product-specifications/${selectedSpec.id}/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchSpecifications();
      const response = await axios.get(`${API}/product-specifications/${selectedSpec.id}`);
      setSelectedSpec(response.data);
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    }
  };

  const handleClone = async (spec) => {
    try {
      const response = await axios.post(`${API}/product-specifications/${spec.id}/clone`);
      toast.success(`Cloned as ${response.data.product_id}`);
      fetchSpecifications();
    } catch (error) {
      console.error("Error cloning specification:", error);
      toast.error("Failed to clone specification");
    }
  };

  const handleDeleteClick = (spec) => {
    setSelectedSpec(spec);
    setDeletePassword("");
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletePassword) {
      toast.error("Please enter the password");
      return;
    }

    try {
      await axios.delete(`${API}/product-specifications/${selectedSpec.id}`, {
        data: { password: deletePassword },
      });
      toast.success("Product specification deleted");
      setShowDeleteDialog(false);
      setSelectedSpec(null);
      setDeletePassword("");
      fetchSpecifications();
    } catch (error) {
      console.error("Error deleting specification:", error);
      toast.error(error.response?.data?.detail || "Failed to delete specification");
    }
  };

  // Selection handling
  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredSpecs.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredSpecs.map((spec) => spec.id));
    }
  };

  // Erase functionality
  const handleEraseClick = (type) => {
    setEraseType(type);
    setErasePassword("");
    setShowEraseDialog(true);
  };

  const handleErase = async () => {
    if (!erasePassword) {
      toast.error("Please enter the password");
      return;
    }

    try {
      if (eraseType === "selected") {
        if (selectedProducts.length === 0) {
          toast.error("No products selected");
          return;
        }
        await axios.post(`${API}/product-specifications/bulk-delete`, {
          ids: selectedProducts,
          password: erasePassword,
        });
        toast.success(`${selectedProducts.length} products erased`);
        setSelectedProducts([]);
      } else if (eraseType === "all") {
        await axios.post(`${API}/product-specifications/delete-all`, {
          password: erasePassword,
        });
        toast.success("All products erased");
      }
      setShowEraseDialog(false);
      setErasePassword("");
      fetchSpecifications();
    } catch (error) {
      console.error("Error erasing:", error);
      toast.error(error.response?.data?.detail || "Failed to erase");
    }
  };

  // Import functionality
  const handleImportClick = () => {
    setShowImportDialog(true);
    setImportFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const response = await axios.post(`${API}/product-specifications/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(response.data.message || "Import successful");
      setShowImportDialog(false);
      setImportFile(null);
      fetchSpecifications();
    } catch (error) {
      console.error("Error importing:", error);
      toast.error(error.response?.data?.detail || "Failed to import");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-12 w-12 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/workorder")} variant="ghost" className="text-gray-400 hover:text-white" data-testid="back-to-workorder">
              <ArrowLeft className="h-5 w-5 mr-2" /> Back
            </Button>
            <Package className="h-8 w-8 text-cyan-400" />
            <h1 className="text-2xl font-bold text-white">
              Prod & Material Info
            </h1>
            <Badge variant="secondary" className="bg-slate-700 text-gray-300">
              {specifications.length} Products
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleImportClick}
              className="border-slate-600 text-gray-300 hover:bg-slate-700"
              data-testid="import-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            {selectedProducts.length > 0 && (
              <Button
                variant="outline"
                onClick={() => handleEraseClick("selected")}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="erase-selected-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Erase Selected ({selectedProducts.length})
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleEraseClick("all")}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              data-testid="erase-all-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Erase All
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/workorder/die-ids")}
              className="border-slate-600 text-gray-300 hover:bg-slate-700"
            >
              <Settings className="h-4 w-4 mr-2" />
              Die ID Store
            </Button>
            <Button
              data-testid="add-new-spec-btn"
              onClick={handleAddNew}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Entry
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="search-input"
              placeholder="Search by Product ID, Company, or Item..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10 bg-slate-800 border-slate-600 text-white placeholder:text-gray-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700 hover:bg-slate-800/50">
                <TableHead className="text-cyan-400 font-bold w-10">
                  <Checkbox
                    checked={selectedProducts.length === filteredSpecs.length && filteredSpecs.length > 0}
                    onCheckedChange={handleSelectAll}
                    className="border-slate-500"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="text-cyan-400 font-bold">Product ID</TableHead>
                <TableHead className="text-cyan-400 font-bold">Company Name</TableHead>
                <TableHead className="text-cyan-400 font-bold">Item Name</TableHead>
                <TableHead className="text-cyan-400 font-bold text-center">Ply</TableHead>
                <TableHead className="text-cyan-400 font-bold text-right">Rate</TableHead>
                <TableHead className="text-cyan-400 font-bold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedSpecs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                    {searchTerm
                      ? "No specifications found matching your search"
                      : "No product specifications yet. Click 'Add New Entry' to create one."}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSpecs.map((spec) => (
                  <TableRow key={spec.id} className="border-slate-700 hover:bg-slate-700/30">
                    <TableCell>
                      <Checkbox
                        checked={selectedProducts.includes(spec.id)}
                        onCheckedChange={() => handleSelectProduct(spec.id)}
                        className="border-slate-500"
                        data-testid={`select-checkbox-${spec.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-white font-medium">{spec.product_id}</TableCell>
                    <TableCell className="text-gray-300">{spec.company_name}</TableCell>
                    <TableCell className="text-gray-300 max-w-xs truncate">{spec.item_name}</TableCell>
                    <TableCell className="text-center text-gray-300">{spec.ply}-Ply</TableCell>
                    <TableCell className="text-right text-gray-300">
                      {spec.rate ? `₹${spec.rate.toFixed(2)}` : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          data-testid={`view-btn-${spec.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(spec)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          title="View"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`edit-btn-${spec.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(spec)}
                          className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`comment-btn-${spec.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleComment(spec)}
                          className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 relative"
                          title="Comments"
                        >
                          <MessageSquare className="h-4 w-4" />
                          {spec.comments?.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                              {spec.comments.length}
                            </span>
                          )}
                        </Button>
                        <Button
                          data-testid={`clone-btn-${spec.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleClone(spec)}
                          className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          title="Clone"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {(spec.primary_image_url || spec.image_link || spec.image_links?.[0]?.url) && (
                          <Button
                            data-testid={`qr-btn-${spec.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrintQR(spec)}
                            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                            title="Print QR Code (Primary Image)"
                          >
                            <QrCode className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          data-testid={`delete-btn-${spec.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(spec)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Label className="text-gray-400 text-sm">Show</Label>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(val) => {
                setItemsPerPage(parseInt(val));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-9 bg-slate-800 border-slate-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="15">15</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <Label className="text-gray-400 text-sm">entries</Label>
          </div>
          <p className="text-sm text-gray-400">
            Showing {filteredSpecs.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
            {Math.min(currentPage * itemsPerPage, filteredSpecs.length)} of{" "}
            {filteredSpecs.length} entries
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="border-slate-600 text-gray-300"
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="border-slate-600 text-gray-300"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-gray-300 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="border-slate-600 text-gray-300"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="border-slate-600 text-gray-300"
              >
                Last
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddEditDialog} onOpenChange={setShowAddEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-cyan-400">
              {editingSpec ? "Edit Product Specification" : "Add New Entry"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingSpec
                ? "Update the product specification details"
                : "Enter the details for the new product specification"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4 py-4">
            {/* Product ID */}
            <div className="space-y-2">
              <Label className="text-gray-300">
                Product ID <span className="text-red-400">*</span>
              </Label>
              <Input
                data-testid="form-product-id"
                value={formData.product_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, product_id: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="#PRID-001"
                readOnly={!editingSpec}
              />
              {!editingSpec && <p className="text-xs text-gray-500">Auto-generated</p>}
            </div>

            {/* Company Name - Searchable Dropdown */}
            <div className="space-y-2">
              <Label className="text-gray-300">
                Company Name <span className="text-red-400">*</span>
              </Label>
              <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={companyOpen}
                    data-testid="form-company-name"
                    className="w-full justify-between bg-slate-800 border-slate-600 text-white hover:bg-slate-700 hover:text-white"
                  >
                    {formData.company_name || "Select company..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0 bg-slate-800 border-slate-600">
                  <Command className="bg-slate-800">
                    <CommandInput 
                      placeholder="Search company..." 
                      className="bg-slate-800 text-white border-slate-600"
                    />
                    <CommandList>
                      <CommandEmpty className="text-gray-400 py-4 text-center">No company found.</CommandEmpty>
                      <CommandGroup className="max-h-[200px] overflow-y-auto">
                        {customers.map((customer) => (
                          <CommandItem
                            key={customer.id}
                            value={customer.company_name}
                            onSelect={(currentValue) => {
                              setFormData((prev) => ({ 
                                ...prev, 
                                company_name: currentValue === formData.company_name ? "" : currentValue 
                              }));
                              setCompanyOpen(false);
                            }}
                            className="text-gray-200 hover:bg-slate-700 cursor-pointer"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.company_name?.toLowerCase() === customer.company_name?.toLowerCase() 
                                  ? "opacity-100 text-cyan-400" 
                                  : "opacity-0"
                              )}
                            />
                            {customer.company_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-gray-300">Date</Label>
              <Input
                data-testid="form-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>

            {/* Item Name */}
            <div className="col-span-2 space-y-2">
              <Label className="text-gray-300">
                Item Name <span className="text-red-400">*</span>
              </Label>
              <Input
                data-testid="form-item-name"
                value={formData.item_name}
                onChange={(e) => setFormData((prev) => ({ ...prev, item_name: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Item name"
              />
            </div>

            {/* Image Link - Upload Multiple Attachments */}
            <div className="col-span-2 space-y-3">
              <Label className="text-gray-300 flex items-center gap-2">
                <Image className="h-4 w-4" />
                Product Images
              </Label>
              
              {/* Display uploaded images with QR codes */}
              {(formData.image_links?.length > 0 || formData.image_link) && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {/* Handle legacy single image_link */}
                  {formData.image_link && !formData.image_links?.some(img => img.url === formData.image_link) && (
                    <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 truncate">Image 1</span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(formData.image_link, '_blank')}
                            className="h-6 w-6 p-0 hover:bg-slate-700"
                            title="View Image"
                          >
                            <Eye className="h-3 w-3 text-cyan-400" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData((prev) => ({ ...prev, image_link: "" }))}
                            className="h-6 w-6 p-0 hover:bg-red-900/30"
                            title="Remove"
                          >
                            <X className="h-3 w-3 text-red-400" />
                          </Button>
                        </div>
                      </div>
                      {/* QR Code */}
                      <div className="flex justify-center bg-white p-2 rounded">
                        <QRCodeSVG 
                          value={formData.image_link} 
                          size={80}
                          level="H"
                        />
                      </div>
                      <p className="text-[10px] text-gray-500 text-center">Scan to view image</p>
                    </div>
                  )}
                  
                  {/* Multiple images */}
                  {formData.image_links?.map((img, index) => {
                    const isPrimary = formData.primary_image_url === img.url;
                    return (
                    <div key={index} className={`bg-slate-800 border rounded-lg p-3 space-y-2 ${isPrimary ? 'border-yellow-500 ring-1 ring-yellow-500/50' : 'border-slate-600'}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 truncate" title={img.filename}>
                          {img.filename?.slice(0, 12) || `Image ${index + 1}`}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(img.url, '_blank')}
                            className="h-6 w-6 p-0 hover:bg-slate-700"
                            title="View Image"
                          >
                            <Eye className="h-3 w-3 text-cyan-400" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newLinks = formData.image_links.filter((_, i) => i !== index);
                              setFormData((prev) => ({
                                ...prev,
                                image_links: newLinks,
                                image_link: prev.image_link === img.url ? "" : prev.image_link,
                                primary_image_url: prev.primary_image_url === img.url ? (newLinks[0]?.url || "") : prev.primary_image_url
                              }));
                            }}
                            className="h-6 w-6 p-0 hover:bg-red-900/30"
                            title="Remove"
                          >
                            <X className="h-3 w-3 text-red-400" />
                          </Button>
                        </div>
                      </div>
                      {/* QR Code */}
                      <div className="flex justify-center bg-white p-2 rounded">
                        <QRCodeSVG 
                          value={img.url} 
                          size={80}
                          level="H"
                        />
                      </div>
                      {/* Set as Primary Button */}
                      <Button
                        type="button"
                        variant={isPrimary ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            primary_image_url: img.url,
                            image_link: img.url
                          }));
                        }}
                        className={`w-full text-xs h-7 ${isPrimary 
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white border-yellow-500' 
                          : 'bg-slate-700 hover:bg-slate-600 border-slate-500 text-gray-300'}`}
                      >
                        {isPrimary ? '★ Primary (Table QR)' : 'Set as Primary'}
                      </Button>
                    </div>
                  )})}
                </div>
              )}
              
              {/* Upload Button */}
              <div className="flex gap-2 items-center">
                <input
                  type="file"
                  id="product-image-upload"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    
                    for (const file of files) {
                      if (file.size > 10 * 1024 * 1024) {
                        toast.error(`${file.name} is too large. Max size is 10MB`);
                        return;
                      }
                    }
                    
                    toast.loading(`Uploading ${files.length} image(s)...`, { id: "image-upload" });
                    
                    try {
                      const uploadedImages = [];
                      
                      for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const formDataUpload = new FormData();
                        formDataUpload.append("file", file);
                        
                        const timestamp = Date.now();
                        const ext = file.name.split('.').pop();
                        const cleanName = formData.product_id || formData.item_name || 'product';
                        const filename = `${cleanName.replace(/\s+/g, '-')}-${i + 1}-${timestamp}.${ext}`;
                        formDataUpload.append("filename", filename);
                        
                        const response = await axios.post(
                          `${API}/upload/attachment`,
                          formDataUpload,
                          { headers: { "Content-Type": "multipart/form-data" } }
                        );
                        
                        if (response.data.success) {
                          uploadedImages.push({
                            url: response.data.direct_link || response.data.shareable_link,
                            filename: filename,
                            file_id: response.data.file_id
                          });
                        }
                      }
                      
                      if (uploadedImages.length > 0) {
                        setFormData((prev) => ({
                          ...prev,
                          image_links: [...(prev.image_links || []), ...uploadedImages],
                          image_link: prev.image_link || uploadedImages[0].url
                        }));
                        toast.success(`${uploadedImages.length} image(s) uploaded!`, { id: "image-upload" });
                      } else {
                        toast.error("Failed to upload images", { id: "image-upload" });
                      }
                    } catch (error) {
                      console.error("Upload error:", error);
                      toast.error(error.response?.data?.detail || "Failed to upload", { id: "image-upload" });
                    }
                    
                    e.target.value = "";
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("product-image-upload")?.click()}
                  className="bg-cyan-600/20 hover:bg-cyan-600/40 border-cyan-500/50 text-cyan-400"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {(formData.image_links?.length > 0 || formData.image_link) ? "Add More Images" : "Add Attachment"}
                </Button>
                {(formData.image_links?.length > 0 || formData.image_link) && (
                  <span className="text-xs text-green-400">
                    {(formData.image_links?.length || 0) + (formData.image_link && !formData.image_links?.some(img => img.url === formData.image_link) ? 1 : 0)} image(s)
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Upload multiple product images (max 10MB each). Each image gets a QR code.
              </p>
            </div>

            {/* Dimensions */}
            <div className="space-y-2">
              <Label className="text-gray-300">
                Dimension Unit <span className="text-gray-500 text-xs">(applies to Length / Breadth / Height)</span>
              </Label>
              <div className="flex gap-2" data-testid="dimension-unit-toggle">
                {["Inches", "MM", "Cm"].map((u) => (
                  <Button
                    key={u}
                    type="button"
                    variant={formData.dimension_unit === u ? "default" : "outline"}
                    onClick={() => setFormData((prev) => ({ ...prev, dimension_unit: u }))}
                    className={
                      formData.dimension_unit === u
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "border-slate-600 text-gray-300 hover:bg-slate-700"
                    }
                    data-testid={`dim-unit-${u.toLowerCase()}`}
                  >
                    {u}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Length ({formData.dimension_unit})</Label>
              <Input
                data-testid="form-length"
                value={formData.length}
                onChange={(e) => setFormData((prev) => ({ ...prev, length: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="e.g., 8 1/4"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Breadth ({formData.dimension_unit})</Label>
              <Input
                data-testid="form-breadth"
                value={formData.breadth}
                onChange={(e) => setFormData((prev) => ({ ...prev, breadth: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="e.g., 6 3/4"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Height ({formData.dimension_unit})</Label>
              <Input
                data-testid="form-height"
                value={formData.height}
                onChange={(e) => setFormData((prev) => ({ ...prev, height: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="e.g., 5 3/4"
              />
            </div>

            {/* Share Across Companies — when checked, picking this spec in WO Creation
                does not lock the customer; the same item can be invoiced to any company. */}
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="share-across-companies-toggle">
                <input
                  type="checkbox"
                  checked={!!formData.shared_across_companies}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, shared_across_companies: e.target.checked }))
                  }
                  className="h-4 w-4 accent-emerald-500"
                  data-testid="share-across-companies-checkbox"
                />
                <span className="text-gray-200 text-sm">
                  Share across companies
                  <span className="block text-gray-500 text-xs">
                    Allow this item to be selected for any company in Work Order Creation
                  </span>
                </span>
              </label>
            </div>

            {/* Reel & Cutting Size */}
            <div className="space-y-2">
              <Label className="text-gray-300">Reel Size</Label>
              <Input
                data-testid="form-reel-size"
                value={formData.reel_size}
                onChange={(e) => setFormData((prev) => ({ ...prev, reel_size: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Reel size"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Cutting Size</Label>
              <Input
                data-testid="form-cutting-size"
                value={formData.cutting_size}
                onChange={(e) => setFormData((prev) => ({ ...prev, cutting_size: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Cutting size"
              />
            </div>

            {/* Ply Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300">Ply</Label>
              <Select value={formData.ply.toString()} onValueChange={handlePlyChange}>
                <SelectTrigger data-testid="form-ply-select" className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="3">3-Ply</SelectItem>
                  <SelectItem value="5">5-Ply</SelectItem>
                  <SelectItem value="7">7-Ply</SelectItem>
                  <SelectItem value="9">9-Ply</SelectItem>
                  <SelectItem value="11">11-Ply</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Top Paper Type */}
            <div className="space-y-2">
              <Label className="text-gray-300">Top Paper Type</Label>
              <Select
                value={formData.top_paper_type || "_none"}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, top_paper_type: v === "_none" ? "" : v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="_none">None</SelectItem>
                  {topPaperTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      <div className="flex items-center justify-between w-full">
                        <span>{type}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!showAddPaperType ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddPaperType(true)}
                  className="text-cyan-400 hover:text-cyan-300 text-xs p-0 h-auto"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Paper Type
                </Button>
              ) : (
                <div className="flex gap-2 mt-2">
                  <Input
                    value={newPaperTypeInput}
                    onChange={(e) => setNewPaperTypeInput(e.target.value)}
                    placeholder="New paper type"
                    className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                    data-testid="new-paper-type-input"
                  />
                  <Button size="sm" onClick={handleAddPaperType} className="h-8 bg-cyan-600 hover:bg-cyan-700">
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowAddPaperType(false);
                      setNewPaperTypeInput("");
                    }}
                    className="h-8"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* Die IDs (Multiple) */}
            <div className="space-y-2">
              <Label className="text-gray-300">Die IDs</Label>
              <div className="space-y-2">
                {(formData.die_ids || []).map((dieId, idx) => {
                  const selectedDie = dieIds.find(d => d.die_id === dieId);
                  const isUsed = selectedDie?.status === "Used";
                  return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <select
                        value={dieId}
                        onChange={(e) => {
                          const newVal = e.target.value;
                          const newDieIds = [...(formData.die_ids || [])];
                          newDieIds[idx] = newVal;
                          setFormData((prev) => ({ ...prev, die_ids: newDieIds }));
                          // Show warning toast if the selected Die ID is "Used"
                          const chosen = dieIds.find(d => d.die_id === newVal);
                          if (chosen && chosen.status === "Used") {
                            toast.warning(`Die ID "${newVal}" is already Used${chosen.used_in_product_id ? ` in Product ${chosen.used_in_product_id}` : ""}. You can still use it, but be aware.`);
                          }
                        }}
                        className={`flex-1 h-10 px-3 rounded-md bg-slate-800 border text-white ${isUsed ? 'border-amber-500' : 'border-slate-600'}`}
                        data-testid={`die-id-select-${idx}`}
                      >
                        <option value="">Select Die ID</option>
                        {dieIds
                          .filter((die) => !formData.company_name || die.company_name?.toLowerCase() === formData.company_name?.toLowerCase() || die.company_name === "")
                          .map((die) => (
                            <option key={die.id} value={die.die_id}>
                              {die.die_id} {die.description ? `- ${die.description}` : ""} {die.status === "Used" ? `(Used: ${die.used_in_product_id || "?"})` : "(Available)"}
                            </option>
                          ))}
                      </select>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          const newDieIds = (formData.die_ids || []).filter((_, i) => i !== idx);
                          setFormData((prev) => ({ ...prev, die_ids: newDieIds }));
                        }}
                        data-testid={`remove-die-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {isUsed && (
                      <p className="text-amber-400 text-xs ml-1">This Die ID is already Used{selectedDie.used_in_product_id ? ` in Product ${selectedDie.used_in_product_id}` : ""}</p>
                    )}
                  </div>
                  );
                })}
                {/* Warning for used die IDs */}
                {(formData.die_ids || []).some(did => dieIds.find(d => d.die_id === did && d.status === "Used")) && (
                  <p className="text-amber-400 text-xs font-medium">Some selected Die IDs are already used in other products</p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData((prev) => ({ ...prev, die_ids: [...(prev.die_ids || []), ""] }))}
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  data-testid="add-die-id-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Die ID
                </Button>
              </div>
            </div>

            {/* Rate */}
            <div className="space-y-2">
              <Label className="text-gray-300">Rate (₹)</Label>
              <Input
                data-testid="form-rate"
                type="number"
                value={formData.rate}
                onChange={(e) => setFormData((prev) => ({ ...prev, rate: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Rate"
              />
            </div>

            {/* Stitching */}
            <div className="space-y-2">
              <Label className="text-gray-300">Stitching</Label>
              <Input
                data-testid="form-stitching"
                value={formData.stitching}
                onChange={(e) => setFormData((prev) => ({ ...prev, stitching: e.target.value }))}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Stitching type"
              />
            </div>

            {/* Calico Color */}
            <div className="space-y-2">
              <Label className="text-gray-300">Calico Color</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.calico_color || "_none"}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, calico_color: v === "_none" ? "" : v }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white flex-1">
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="_none">None</SelectItem>
                    {calicoColors.map((color) => (
                      <SelectItem key={color} value={color}>
                        <div className="flex items-center justify-between w-full">
                          <span>{color}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!showAddCalicoColor ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600 text-gray-300"
                    onClick={() => setShowAddCalicoColor(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                ) : (
                  <div className="flex gap-1">
                    <Input
                      value={newCalicoColorInput}
                      onChange={(e) => setNewCalicoColorInput(e.target.value)}
                      className="w-20 h-9 bg-slate-700 border-slate-600 text-white text-sm"
                      placeholder="New"
                      onKeyDown={(e) => e.key === "Enter" && handleAddCalicoColor()}
                    />
                    <Button size="sm" className="h-9 bg-cyan-500 text-black" onClick={handleAddCalicoColor}>
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9"
                      onClick={() => { setShowAddCalicoColor(false); setNewCalicoColorInput(""); }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Number Calico */}
            <div className="space-y-2">
              <Label className="text-gray-300">Number Calico</Label>
              <div className="flex gap-2">
                <Select
                  value={formData.number_calico || "_none"}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, number_calico: v === "_none" ? "" : v }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white flex-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="_none">None</SelectItem>
                    {numberCalicoOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!showAddNumberCalico ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600 text-gray-300"
                    onClick={() => setShowAddNumberCalico(true)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                ) : (
                  <div className="flex gap-1">
                    <Input
                      value={newNumberCalicoInput}
                      onChange={(e) => setNewNumberCalicoInput(e.target.value)}
                      className="w-16 h-9 bg-slate-700 border-slate-600 text-white text-sm"
                      placeholder="New"
                      onKeyDown={(e) => e.key === "Enter" && handleAddNumberCalico()}
                    />
                    <Button size="sm" className="h-9 bg-cyan-500 text-black" onClick={handleAddNumberCalico}>
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9"
                      onClick={() => { setShowAddNumberCalico(false); setNewNumberCalicoInput(""); }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* GSM Configuration */}
            <div className="col-span-3 space-y-3">
              <Label className="text-gray-300 text-lg font-semibold">
                GSM Configuration ({formData.ply}-Ply) - Linked to Raw Materials
              </Label>
              <div className="grid grid-cols-4 gap-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                {getGSMConfig(formData.ply).map((item, index) => (
                  <div key={`gsm-${formData.ply}-${index}`} className="space-y-1">
                    <Label className="text-gray-400 text-sm">{item.name}</Label>
                    <Popover 
                      open={gsmDropdownOpen[index]} 
                      onOpenChange={(open) => setGsmDropdownOpen(prev => ({ ...prev, [index]: open }))}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between bg-slate-700 border-slate-600 text-white hover:bg-slate-600 text-sm h-10"
                        >
                          {formData.gsm_config[index]?.gsm || "Select GSM..."}
                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[250px] p-0 bg-gray-900 border-slate-700" align="start">
                        <Command className="bg-gray-900">
                          <CommandInput placeholder="Search raw material..." className="text-white h-9" />
                          <CommandList>
                            <CommandEmpty>
                              <div className="p-2">
                                <p className="text-sm text-gray-400 mb-2">No matching material</p>
                                <Input
                                  placeholder="Enter GSM manually"
                                  className="bg-slate-800 border-slate-600 text-white text-sm h-8"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleGSMChange(index, e.target.value);
                                      setGsmDropdownOpen(prev => ({ ...prev, [index]: false }));
                                    }
                                  }}
                                />
                              </div>
                            </CommandEmpty>
                            <CommandGroup heading="Raw Materials">
                              {rawMaterials.map((material) => (
                                <CommandItem
                                  key={material.id}
                                  value={`${material.product_code || ''} ${material.item_name || ''} ${material.product_type || ''}`}
                                  onSelect={() => {
                                    handleGSMChange(index, material.product_code || material.item_name || '');
                                    setGsmDropdownOpen(prev => ({ ...prev, [index]: false }));
                                  }}
                                  className="text-white hover:bg-slate-700 cursor-pointer"
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      formData.gsm_config[index]?.gsm === (material.product_code || material.item_name)
                                        ? "opacity-100"
                                        : "opacity-0"
                                    }`}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{material.product_code || material.item_name}</span>
                                    <span className="text-xs text-gray-400">{material.product_type}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">Select from Store Database or enter manually</p>
            </div>

            {/* Printing - Yes/No Dropdown */}
            <div className="space-y-2">
              <Label className="text-gray-300">Printing</Label>
              <Select
                value={formData.printing_enabled}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    printing_enabled: value,
                    colors: value === "No" ? [] : prev.colors,
                    no_of_colors: value === "No" ? "" : prev.no_of_colors,
                  }))
                }
              >
                <SelectTrigger data-testid="form-printing-select" className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Conditional: Color & No of Colors */}
            {formData.printing_enabled === "Yes" && (
              <div className="col-span-2 space-y-4 p-4 bg-slate-800/30 rounded-lg border border-cyan-500/30">
                <Label className="text-cyan-400 font-semibold">Printing Details</Label>

                {/* Color Multi-Select */}
                <div className="space-y-2">
                  <Label className="text-gray-300">Color (Select multiple)</Label>
                  <div className="flex flex-wrap gap-2">
                    {availablePrintColors.map((color) => (
                      <Badge
                        key={color}
                        variant={formData.colors?.includes(color) ? "default" : "outline"}
                        className={`cursor-pointer transition-all ${
                          formData.colors?.includes(color)
                            ? "bg-cyan-500 text-black hover:bg-cyan-600"
                            : "border-slate-500 text-gray-300 hover:border-cyan-400"
                        }`}
                        onClick={() => handleColorToggle(color)}
                      >
                        {formData.colors?.includes(color) && <Check className="h-3 w-3 mr-1" />}
                        {color}
                      </Badge>
                    ))}
                    {!showAddColor ? (
                      <Badge
                        variant="outline"
                        className="cursor-pointer border-dashed border-slate-500 text-gray-400 hover:border-cyan-400"
                        onClick={() => setShowAddColor(true)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Input
                          value={newColorInput}
                          onChange={(e) => setNewColorInput(e.target.value)}
                          className="h-7 w-20 text-sm bg-slate-700 border-slate-600 text-white"
                          placeholder="Color"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddNewColor();
                            if (e.key === "Escape") { setShowAddColor(false); setNewColorInput(""); }
                          }}
                          autoFocus
                        />
                        <Button size="sm" className="h-7 px-2 bg-cyan-500 text-black" onClick={handleAddNewColor}>
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                          onClick={() => { setShowAddColor(false); setNewColorInput(""); }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* No of Colors */}
                <div className="space-y-2">
                  <Label className="text-gray-300">No of Colors</Label>
                  <Input
                    data-testid="form-no-of-colors"
                    type="number"
                    value={formData.no_of_colors}
                    onChange={(e) => setFormData((prev) => ({ ...prev, no_of_colors: e.target.value }))}
                    className="bg-slate-700 border-slate-600 text-white w-32"
                    placeholder="e.g., 2"
                    min="1"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEditDialog(false)} className="border-slate-600 text-gray-300">
              Cancel
            </Button>
            <Button
              data-testid="save-spec-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingSpec ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={showCommentDialog} onOpenChange={setShowCommentDialog}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-purple-400">
              Comments - {selectedSpec?.product_id}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="max-h-60 overflow-y-auto space-y-2">
              {selectedSpec?.comments?.length > 0 ? (
                selectedSpec.comments.map((comment) => (
                  <div key={comment.id} className="p-3 bg-slate-800 rounded-lg border border-slate-700">
                    <div className="flex justify-between items-start">
                      <p className="text-gray-300 text-sm flex-1">{comment.text}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-400 hover:text-red-300 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(comment.created_at).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No comments yet</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Add Comment</Label>
              <Textarea
                data-testid="new-comment-input"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Enter your comment..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommentDialog(false)} className="border-slate-600 text-gray-300">
              Close
            </Button>
            <Button
              data-testid="add-comment-btn"
              onClick={handleAddComment}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
            >
              Add Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-400">Delete Product Specification</DialogTitle>
            <DialogDescription className="text-gray-400">
              You are about to delete{" "}
              <span className="text-white font-semibold">{selectedSpec?.product_id}</span>. This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Enter password to confirm deletion</Label>
              <PasswordInput
                data-testid="delete-password-input"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-slate-600 text-gray-300">
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete-btn"
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white font-bold"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Print Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-cyan-400 flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code for Primary Image
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Scan this QR code to access the product image
            </DialogDescription>
          </DialogHeader>

          <div className="py-6 flex flex-col items-center">
            <div className="bg-white p-4 rounded-lg" ref={qrRef}>
              {(qrSpec?.primary_url || qrSpec?.primary_image_url || qrSpec?.image_link) && (
                <QRCodeSVG 
                  value={qrSpec.primary_url || qrSpec.primary_image_url || qrSpec.image_link} 
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              )}
            </div>
            <div className="mt-4 text-center">
              <p className="text-white font-bold">{qrSpec?.product_id}</p>
              <p className="text-gray-400 text-sm">{qrSpec?.item_name}</p>
              <p className="text-yellow-500 text-xs mt-1">★ Primary Image</p>
            </div>
            <p className="text-xs text-gray-500 mt-2 max-w-xs text-center break-all">
              {qrSpec?.primary_url || qrSpec?.primary_image_url || qrSpec?.image_link}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQRDialog(false)} className="border-slate-600 text-gray-300">
              Close
            </Button>
            <Button
              data-testid="print-qr-btn"
              onClick={printQRCode}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Erase Confirmation Dialog */}
      <Dialog open={showEraseDialog} onOpenChange={setShowEraseDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-red-400">
              {eraseType === "selected" ? "Erase Selected Products" : "Erase All Products"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {eraseType === "selected"
                ? `You are about to erase ${selectedProducts.length} selected products. This action cannot be undone.`
                : "You are about to erase ALL products. This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Enter password to confirm</Label>
              <PasswordInput
                data-testid="erase-password-input"
                value={erasePassword}
                onChange={(e) => setErasePassword(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white"
                placeholder="Password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEraseDialog(false);
                setErasePassword("");
              }}
              className="border-slate-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-erase-btn"
              onClick={handleErase}
              className="bg-red-500 hover:bg-red-600 text-white font-bold"
            >
              Erase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl text-cyan-400">Import Products</DialogTitle>
            <DialogDescription className="text-gray-400">
              Upload an Excel file (.xlsx) to import product specifications.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Select File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="bg-slate-800 border-slate-600 text-white file:bg-slate-700 file:text-white file:border-0 file:mr-4 file:py-2 file:px-4 cursor-pointer"
                data-testid="import-file-input"
                ref={fileInputRef}
              />
              {importFile && (
                <p className="text-sm text-green-400">Selected: {importFile.name}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                setImportFile(null);
              }}
              className="border-slate-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-import-btn"
              onClick={handleImport}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              disabled={!importFile}
            >
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProdMaterialInfo;
